import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, EmptyState } from "@/components/system";

type AccountRow = {
  id: string;
  display_name: string;
  institution: string | null;
  account_kind: string;
  currency: string;
  current_balance: number | null;
  balance_as_of: string | null;
  closed: boolean;
  investment_provider: string | null;
  investment_tier: string | null;
  pfic_risk: boolean;
  region: string | null;
  notes: string | null;
};

const ACCOUNT_KINDS = [
  "checking",
  "savings",
  "credit_card",
  "cash",
  "investment",
  "pension",
  "loan",
  "mortgage",
  "liability",
  "travel_card",
  "reward",
] as const;

const CURRENCIES = ["CHF", "EUR", "USD", "GBP", "JPY", "SEK", "DKK", "AUD", "CAD"];

export function FinanceAccounts() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const accounts = useQuery({
    queryKey: ["finance_accounts", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_account")
        .select("*")
        .eq("user_id", userId!)
        .order("display_name");
      if (error) throw error;
      return ((data ?? []) as AccountRow[]).filter((a) => !a.closed);
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header
        style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}
      >
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-s)",
          }}
        >
          Accounts
        </h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setOpen(true)}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          + Add account
        </button>
      </header>

      {accounts.isLoading ? (
        <SkeletonLoader variant="card" height={140} />
      ) : (accounts.data ?? []).length === 0 ? (
        <EmptyState
          headline="No accounts yet."
          sub="Add your first account — checking, credit card, IBKR, etc."
          cta={
            <button onClick={() => setOpen(true)} style={addBtnStyle}>
              Add account
            </button>
          }
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "var(--space-4)",
          }}
        >
          {(accounts.data ?? []).map((a) => (
            <AccountCard key={a.id} account={a} />
          ))}
        </div>
      )}

      {open && (
        <AddAccountSheet
          userId={userId!}
          onClose={() => setOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["finance_accounts", userId] });
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AccountCard({ account }: { account: AccountRow }) {
  return (
    <article
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        background: "var(--bg-2)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
        <strong style={{ fontWeight: 500 }}>{account.display_name}</strong>
        <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
          {account.account_kind.replace(/_/g, " ")}
        </span>
      </header>
      {account.institution && (
        <span style={{ fontSize: "var(--type-body-s)", color: "var(--fg-3)" }}>{account.institution}</span>
      )}
      <div className="mono" style={{ fontSize: 22, fontWeight: 500 }}>
        {formatMoney(account.current_balance, account.currency)}
      </div>
      {account.pfic_risk && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            background: "var(--severity-warn-wash, #fdf6e3)",
            color: "var(--severity-warn, #b07c2a)",
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <AlertTriangle size={14} />
          PFIC risk — review before US tax residency
        </div>
      )}
      {account.investment_tier && (
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          Tracking: {account.investment_tier.replace(/_/g, " ")}
        </span>
      )}
    </article>
  );
}

function AddAccountSheet({
  userId,
  onClose,
  onSaved,
}: {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [institution, setInstitution] = useState("");
  const [kind, setKind] = useState<typeof ACCOUNT_KINDS[number]>("checking");
  const [currency, setCurrency] = useState("CHF");
  const [balance, setBalance] = useState("");
  const [region, setRegion] = useState<"CH" | "EU" | "US" | "UK" | "OTHER">("CH");
  const [pfic, setPfic] = useState(false);

  const mutate = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as never as { from: (t: string) => any }).from("finance_account").insert({
        user_id: userId,
        display_name: displayName.trim() || "Untitled",
        institution: institution.trim() || null,
        account_kind: kind,
        currency,
        current_balance: balance ? Number(balance) : null,
        balance_as_of: balance ? new Date().toISOString() : null,
        region,
        pfic_risk: pfic,
      });
      if (error) throw error;
    },
    onSuccess: onSaved,
  });

  return (
    <Sheet onClose={onClose} title="Add account">
      <Field label="Name">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} placeholder="e.g. UBS Checking" />
      </Field>
      <Field label="Institution">
        <input value={institution} onChange={(e) => setInstitution(e.target.value)} style={inputStyle} placeholder="UBS / IBKR / Schwab" />
      </Field>
      <Field label="Kind">
        <select value={kind} onChange={(e) => setKind(e.target.value as typeof ACCOUNT_KINDS[number])} style={inputStyle}>
          {ACCOUNT_KINDS.map((k) => (
            <option key={k} value={k}>{k.replace(/_/g, " ")}</option>
          ))}
        </select>
      </Field>
      <Field label="Currency">
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={inputStyle}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Balance">
        <input
          type="number"
          inputMode="decimal"
          value={balance}
          onChange={(e) => setBalance(e.target.value)}
          style={inputStyle}
          placeholder="0.00"
        />
      </Field>
      <Field label="Region">
        <select value={region} onChange={(e) => setRegion(e.target.value as typeof region)} style={inputStyle}>
          <option value="CH">Switzerland</option>
          <option value="EU">EU</option>
          <option value="US">United States</option>
          <option value="UK">UK</option>
          <option value="OTHER">Other</option>
        </select>
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--type-body-s)" }}>
        <input type="checkbox" checked={pfic} onChange={(e) => setPfic(e.target.checked)} />
        Holds EU-domiciled securities (PFIC risk on US tax residency)
      </label>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={btnGhostStyle}>Cancel</button>
        <button onClick={() => mutate.mutate()} disabled={mutate.isPending} style={addBtnStyle}>
          {mutate.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </Sheet>
  );
}

function Sheet({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <>
      <div onClick={onClose} style={overlayStyle} />
      <aside style={sheetStyle} role="dialog" aria-modal="true">
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontWeight: 500, fontSize: 22 }}>
            {title}
          </h2>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">×</button>
        </header>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
      </aside>
    </>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  background: "var(--bg)",
  color: "var(--fg)",
  fontSize: "var(--type-body-s)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  zIndex: 50,
};

const sheetStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: "92vh",
  overflowY: "auto",
  background: "var(--bg-2)",
  borderTop: "1px solid var(--hair)",
  borderTopLeftRadius: 18,
  borderTopRightRadius: 18,
  padding: "var(--space-5)",
  paddingBottom: "calc(var(--space-5) + env(safe-area-inset-bottom))",
  zIndex: 51,
  width: "100%",
  maxWidth: 560,
  margin: "0 auto",
};

const addBtnStyle: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "none",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

const btnGhostStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid var(--hair-2)",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg-2)",
  border: "none",
  fontSize: 28,
  lineHeight: 1,
  cursor: "pointer",
  padding: 0,
};
